/**
 * FCD Spice Addons — fetch Spice JSON + Storefront product details, render custom HTML.
 * On Add to Cart with addons selected: takes over the add (PDP product once, then the
 * selected addon variants) to /cart/add.js. Without addons the theme form adds as usual.
 * Keeps the native Spice block in sync (hidden) when present.
 */
(function () {
  var ROOT_SEL = '[data-fcd-spice-addons]';
  // StandardEvents.cartLinesUpdate from @shopify/events (this is a classic script, no import)
  var STANDARD_CART_LINES_UPDATE = 'shopify:cart:lines-update';
  var PRODUCT_FORM_SEL =
    '.custom-product-right form.product-form, .custom-product-right form[data-type="add-to-cart-form"]';
  var PRODUCTS_QUERY =
    'query FcdSpiceProducts($ids: [ID!]!) {\n' +
    '  nodes(ids: $ids) {\n' +
    '    ... on Product {\n' +
    '      id\n' +
    '      title\n' +
    '      handle\n' +
    '      availableForSale\n' +
    '      featuredImage { url altText }\n' +
    '      variants(first: 50) {\n' +
    '        nodes {\n' +
    '          id\n' +
    '          title\n' +
    '          availableForSale\n' +
    '          price { amount currencyCode }\n' +
    '          compareAtPrice { amount currencyCode }\n' +
    '        }\n' +
    '      }\n' +
    '    }\n' +
    '  }\n' +
    '}';

  var CHEVRON_SVG =
    '<svg class="fcd-spice-addons__chevron-icon" width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function gidToNumeric(gid) {
    if (!gid) return '';
    var parts = String(gid).split('/');
    return parts[parts.length - 1] || '';
  }

  function toProductGid(id) {
    return 'gid://shopify/Product/' + String(id);
  }

  function formatMoney(amount) {
    var n = Number(amount);
    if (Number.isNaN(n)) return '';
    return (
      '$' +
      n.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    );
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function getConfig(root) {
    var el = root.querySelector('[data-fcd-spice-config]');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent || '{}');
    } catch (e) {
      return {};
    }
  }

  function resolveSpiceHost(cfg) {
    if (cfg.spiceHost) return cfg.spiceHost.replace(/\/$/, '');
    if (window.sg_addon_settings_config && window.sg_addon_settings_config.host) {
      return String(window.sg_addon_settings_config.host).replace(/\/$/, '');
    }
    return 'https://spa.spicegems.com';
  }

  function resolveShop(cfg) {
    if (cfg.shop) return cfg.shop;
    if (window.Shopify && Shopify.shop) return Shopify.shop;
    if (window.sg_addon_settings_config && window.sg_addon_settings_config.shopName) {
      return window.sg_addon_settings_config.shopName;
    }
    return '';
  }

  function resolveCollectionIds(cfg) {
    if (cfg.collectionIds) return String(cfg.collectionIds);
    var hidden = document.getElementById('sgcollections');
    if (hidden && hidden.value) return hidden.value;
    if (window.sg_addons_coll_ids && window.sg_addons_coll_ids.ids) {
      return String(window.sg_addons_coll_ids.ids);
    }
    return '';
  }

  function isOptionalGroup(title) {
    return /optional/i.test(String(title || ''));
  }

  function optionLabel(name, price) {
    return String(name) + ' / ' + formatMoney(price);
  }

  async function fetchSpiceGroups(cfg) {
    var host = resolveSpiceHost(cfg);
    var shop = resolveShop(cfg);
    var productId = cfg.productId;
    var collectionIds = resolveCollectionIds(cfg);
    var hasSetting =
      cfg.hasSetting != null
        ? cfg.hasSetting
        : window.sg_addon_settings_config && window.sg_addon_settings_config.hasSetting
          ? window.sg_addon_settings_config.hasSetting
          : 1;

    if (!productId || !shop) {
      throw new Error('Missing product id or shop for Spice API.');
    }

    var url =
      host +
      '/api/product/' +
      encodeURIComponent(productId) +
      '?collection_id=' +
      encodeURIComponent(collectionIds) +
      '&shop=' +
      encodeURIComponent(shop) +
      '&hasSetting=' +
      encodeURIComponent(hasSetting);

    var res = await fetch(url, { credentials: 'omit' });
    if (!res.ok) throw new Error('Spice API HTTP ' + res.status);
    var data = await res.json();
    if (!data || data.success === false) {
      throw new Error('Spice API returned no addons.');
    }
    return data;
  }

  async function fetchProductsByIds(ids, token, apiVersion) {
    if (!ids.length) return {};
    if (!token) throw new Error('Missing Storefront access token.');

    var endpoint = '/api/' + (apiVersion || '2025-01') + '/graphql.json';
    var res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token
      },
      body: JSON.stringify({
        query: PRODUCTS_QUERY,
        variables: { ids: ids.map(toProductGid) }
      })
    });

    if (!res.ok) throw new Error('Storefront HTTP ' + res.status);
    var json = await res.json();
    if (json.errors && json.errors.length) {
      throw new Error(json.errors[0].message || 'Storefront GraphQL error');
    }

    var map = {};
    var nodes = (json.data && json.data.nodes) || [];
    nodes.forEach(function (node) {
      if (!node || !node.id) return;
      map[gidToNumeric(node.id)] = node;
    });
    return map;
  }

  function flattenOptions(group) {
    var options = [];

    if (group.optional) {
      options.push({
        key: 'none',
        productId: '',
        variantId: '',
        price: 0,
        name: 'None',
        label: optionLabel('None', 0),
        available: true
      });
    }

    // One product with many variants → each variant is a radio option
    if (
      group.products.length === 1 &&
      group.products[0].variants &&
      group.products[0].variants.length > 1
    ) {
      var product = group.products[0];
      product.variants.forEach(function (variant) {
        var name =
          variant.title && variant.title !== 'Default Title'
            ? variant.title
            : product.title;
        options.push({
          key: product.productId + ':' + variant.id,
          productId: product.productId,
          variantId: variant.id,
          price: variant.price,
          name: name,
          label: optionLabel(name, variant.price),
          available: !!variant.available
        });
      });
      return options;
    }

    // Multiple products (or single with one variant) → one option per product
    group.products.forEach(function (product) {
      var variant = (product.variants && product.variants[0]) || {};
      var name = product.title;
      if (
        group.products.length === 1 &&
        variant.title &&
        variant.title !== 'Default Title'
      ) {
        name = variant.title;
      }
      options.push({
        key: product.productId + ':' + (variant.id || '0'),
        productId: product.productId,
        variantId: variant.id || '',
        price: variant.price || 0,
        name: name,
        label: optionLabel(name, variant.price || 0),
        available: product.available !== false && variant.available !== false
      });
    });

    return options;
  }

  function buildModel(spiceData, productsById) {
    var groups = (spiceData.groups || []).slice().sort(function (a, b) {
      return (a.priority || 0) - (b.priority || 0);
    });

    return groups
      .map(function (group) {
        var addons = (group.addons || []).slice().sort(function (a, b) {
          return (a.priority || 0) - (b.priority || 0);
        });

        var title = group.title || group.name;
        var products = addons
          .map(function (addon) {
            var product = productsById[String(addon.product_id)];
            if (!product) return null;
            var variants = ((product.variants && product.variants.nodes) || []).map(
              function (v) {
                return {
                  id: gidToNumeric(v.id),
                  title: v.title,
                  available: !!v.availableForSale,
                  price: v.price && v.price.amount,
                  compareAt:
                    v.compareAtPrice && v.compareAtPrice.amount
                      ? v.compareAtPrice.amount
                      : null
                };
              }
            );
            return {
              productId: String(addon.product_id),
              title: product.title,
              handle: product.handle,
              url: '/products/' + product.handle,
              available: !!product.availableForSale,
              variants: variants
            };
          })
          .filter(Boolean);

        var modelGroup = {
          groupId: group.group_id,
          name: group.name,
          title: title,
          optional: isOptionalGroup(title),
          products: products
        };
        modelGroup.options = flattenOptions(modelGroup);
        return modelGroup;
      })
      .filter(function (group) {
        return group.options && group.options.length > 0;
      });
  }

  function getQuantity() {
    var qty = document.querySelector(
      '#Quantity, .custom-product-right .product-form input[name="quantity"]'
    );
    var n = qty ? parseInt(qty.value, 10) : 1;
    return Number.isFinite(n) && n > 0 ? n : 1;
  }

  function getSelectedAddonsTotal(root) {
    var total = 0;
    root.querySelectorAll('[data-fcd-spice-option]:checked').forEach(function (input) {
      var price = Number(input.getAttribute('data-price'));
      if (!Number.isNaN(price)) total += price;
    });
    return total;
  }

  /**
   * Price of the variant currently in the product form, from the section's ProductJson.
   * basePriceCents in the config is only the variant the page was loaded with.
   */
  function getCurrentVariantPriceCents() {
    var id = getMainVariantId(getProductForm());
    var json = document.querySelector('script[id^="ProductJson-"]');
    if (!id || !json) return null;
    try {
      var product = JSON.parse(json.textContent || '{}');
      var variant = (product.variants || []).find(function (v) {
        return String(v.id) === id;
      });
      return variant && typeof variant.price === 'number' ? variant.price : null;
    } catch (err) {
      return null;
    }
  }

  var writingPrice = false;

  function updateDisplayedPrice(root) {
    var cfg = root._fcdSpiceConfig || getConfig(root);
    var baseCents = getCurrentVariantPriceCents();
    if (baseCents === null) baseCents = Number(cfg.basePriceCents);
    if (Number.isNaN(baseCents)) {
      var baseLegacy = Number(cfg.basePrice);
      baseCents = Number.isNaN(baseLegacy) ? 0 : Math.round(baseLegacy * 100);
    }

    var addonsCents = Math.round(getSelectedAddonsTotal(root) * 100);
    // Simple Bundles option surcharges (simple-bundles-bridge.js), charged in the cart too.
    var optionsCents =
      typeof window.fcdSimpleBundlesSurchargeCents === 'function'
        ? window.fcdSimpleBundlesSurchargeCents()
        : 0;
    var unitTotalCents = baseCents + addonsCents + optionsCents;
    var totalCents = unitTotalCents * getQuantity();
    var formatted = formatMoney(totalCents / 100);
    var selector =
      cfg.priceSelector || '.custom-product-right .product-price span.price';

    writingPrice = true;
    document.querySelectorAll(selector).forEach(function (el) {
      if (el.textContent !== formatted) el.textContent = formatted;
    });
    writingPrice = false;

    document
      .querySelectorAll('.sg-pro-subtotal-span .money, .sg-pro-subtotal-span')
      .forEach(function (el) {
        if (el.matches('.sg-pro-subtotal-span') && el.querySelector('.money')) {
          return;
        }
        el.textContent = formatted;
      });

    root.dispatchEvent(
      new CustomEvent('apollo:fcd-spice-price-updated', {
        bubbles: true,
        detail: {
          baseCents: baseCents,
          addonsCents: addonsCents,
          unitTotalCents: unitTotalCents,
          totalCents: totalCents,
          total: totalCents / 100
        }
      })
    );
  }

  /**
   * After a variant change, simple-bundles-bridge and the variant-selector script in
   * main-product-custom both re-render the price block from the section HTML — the bare
   * variant price, while the selected addons stay selected and still go into the cart. Put
   * the addons back on top whenever something else rewrites the price.
   */
  function watchPriceRewrites(root) {
    var container = document.querySelector('.custom-product-right');
    if (!container || root._fcdPriceWatch) return;
    // Observer callbacks run before the next paint, so correcting here means the bare
    // variant price never shows. updateDisplayedPrice only writes when the text differs,
    // so its own write doesn't loop back here.
    root._fcdPriceWatch = new MutationObserver(function (mutations) {
      if (writingPrice) return;
      var touchesPrice = mutations.some(function (m) {
        var node = m.target.nodeType === 1 ? m.target : m.target.parentElement;
        return node && node.closest && node.closest('.product-price');
      });
      if (touchesPrice) updateDisplayedPrice(root);
    });
    root._fcdPriceWatch.observe(container, { childList: true, subtree: true, characterData: true });
  }

  function updateGroupSelectedLabel(groupEl) {
    var selectedText = groupEl.querySelector('[data-fcd-spice-selected]');
    if (!selectedText) return;
    var checked = groupEl.querySelector('[data-fcd-spice-option]:checked');
    if (!checked) {
      selectedText.textContent = 'Selected: —';
      return;
    }
    selectedText.textContent =
      'Selected: ' + (checked.getAttribute('data-label') || checked.value);
  }

  function renderOption(option, groupId, index) {
    var inputId = 'fcd-spice-' + groupId + '-' + index;
    return (
      '<label class="fcd-spice-addons__option" for="' +
      escapeHtml(inputId) +
      '">' +
      '<input' +
      ' type="radio"' +
      ' class="fcd-spice-addons__radio"' +
      ' name="fcd-spice-group-' +
      escapeHtml(groupId) +
      '"' +
      ' id="' +
      escapeHtml(inputId) +
      '"' +
      ' data-fcd-spice-option' +
      ' data-product-id="' +
      escapeHtml(option.productId) +
      '"' +
      ' data-variant-id="' +
      escapeHtml(option.variantId) +
      '"' +
      ' data-price="' +
      escapeHtml(option.price) +
      '"' +
      ' data-label="' +
      escapeHtml(option.label) +
      '"' +
      ' value="' +
      escapeHtml(option.key) +
      '"' +
      (option.available ? '' : ' disabled') +
      '>' +
      '<span class="fcd-spice-addons__option-text">' +
      escapeHtml(option.label) +
      '</span>' +
      '</label>'
    );
  }

  function renderHtml(model) {
    if (!model.length) {
      return '';
    }

    return (
      '<div class="fcd-spice-addons__groups">' +
      model
        .map(function (group, groupIndex) {
          var open = groupIndex === 0;

          var optionsHtml = group.options
            .map(function (option, index) {
              return renderOption(option, group.groupId, index);
            })
            .join('');

          return (
            '<section class="fcd-spice-addons__group" data-group-id="' +
            escapeHtml(group.groupId) +
            '" data-optional="' +
            (group.optional ? 'true' : 'false') +
            '">' +
            '<h4 class="fcd-spice-addons__group-title">' +
            escapeHtml(group.title) +
            '</h4>' +
            '<div class="fcd-spice-addons__dropdown' +
            (open ? ' is-open' : '') +
            '">' +
            '<button type="button" class="fcd-spice-addons__trigger" data-fcd-spice-toggle aria-expanded="' +
            (open ? 'true' : 'false') +
            '">' +
            '<span class="fcd-spice-addons__selected-text" data-fcd-spice-selected>Selected: —</span>' +
            CHEVRON_SVG +
            '</button>' +
            '<div class="fcd-spice-addons__panel"' +
            (open ? '' : ' hidden') +
            '>' +
            '<div class="fcd-spice-addons__grid">' +
            optionsHtml +
            '</div>' +
            '</div>' +
            '</div>' +
            '</section>'
          );
        })
        .join('') +
      '</div>'
    );
  }

  function syncToSpice(productId, checked, variantId) {
    if (!productId) return;

    var checkbox = document.getElementById('checkbox_' + productId);
    if (checkbox) {
      if (checkbox.checked !== checked) {
        checkbox.checked = checked;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (checked) {
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    if (!variantId || !checked) return;

    var select = document.getElementById('variant_' + productId);
    if (select && select.tagName === 'SELECT') {
      if (String(select.value) !== String(variantId)) {
        select.value = String(variantId);
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    var hidden = document.querySelector(
      '#spice_product_main_block_' +
        productId +
        ' input[name="spice_select_variant_id"]'
    );
    if (hidden) hidden.value = String(variantId);
  }

  function syncGroupToSpice(groupEl) {
    var checked = groupEl.querySelector('[data-fcd-spice-option]:checked');
    var selectedProductId = checked ? checked.getAttribute('data-product-id') : '';
    var selectedVariantId = checked ? checked.getAttribute('data-variant-id') : '';

    var seen = {};
    groupEl.querySelectorAll('[data-fcd-spice-option]').forEach(function (input) {
      var productId = input.getAttribute('data-product-id');
      if (!productId || seen[productId]) return;
      seen[productId] = true;
      var isSelected = productId === selectedProductId;
      syncToSpice(
        productId,
        isSelected,
        isSelected ? selectedVariantId : null
      );
    });
  }

  function setDropdownOpen(dropdown, open) {
    var trigger = dropdown.querySelector('[data-fcd-spice-toggle]');
    var panel = dropdown.querySelector('.fcd-spice-addons__panel');
    dropdown.classList.toggle('is-open', open);
    if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (panel) panel.hidden = !open;
  }

  function syncOptionVisuals(groupEl) {
    if (!groupEl) return;
    groupEl.querySelectorAll('.fcd-spice-addons__option').forEach(function (option) {
      var radio = option.querySelector('[data-fcd-spice-option]');
      var selected = !!(radio && radio.checked);
      option.classList.toggle('is-selected', selected);
      if (!radio) return;
      // Keep attribute in sync so custom :checked styles repaint after preventDefault
      if (selected) {
        radio.setAttribute('checked', 'checked');
      } else {
        radio.removeAttribute('checked');
      }
    });
  }

  function applyGroupSelection(root, groupEl) {
    syncOptionVisuals(groupEl);
    updateGroupSelectedLabel(groupEl);
    syncGroupToSpice(groupEl);
    updateDisplayedPrice(root);
  }

  function toggleOptionSelection(root, input, wasChecked) {
    if (!input || input.disabled) return;

    var groupEl = input.closest('.fcd-spice-addons__group');
    if (!groupEl) return;

    groupEl.querySelectorAll('[data-fcd-spice-option]').forEach(function (radio) {
      radio.checked = false;
    });

    // wasChecked must come from before the browser flips the radio on input clicks
    if (!wasChecked) {
      input.checked = true;
    }

    applyGroupSelection(root, groupEl);
  }

  function bindInteractions(root) {
    var pendingOption = null;

    root.addEventListener('pointerdown', function (event) {
      var option = event.target.closest('.fcd-spice-addons__option');
      if (!option || !root.contains(option)) {
        pendingOption = null;
        return;
      }
      var input = option.querySelector('[data-fcd-spice-option]');
      if (!input || input.disabled) {
        pendingOption = null;
        return;
      }
      pendingOption = {
        input: input,
        wasChecked: !!input.checked
      };
    });

    root.addEventListener('click', function (event) {
      var toggle = event.target.closest('[data-fcd-spice-toggle]');
      if (toggle && root.contains(toggle)) {
        event.preventDefault();
        pendingOption = null;
        var dropdown = toggle.closest('.fcd-spice-addons__dropdown');
        if (dropdown) {
          setDropdownOpen(dropdown, !dropdown.classList.contains('is-open'));
        }
        return;
      }

      var option = event.target.closest('.fcd-spice-addons__option');
      if (!option || !root.contains(option)) return;

      var input = option.querySelector('[data-fcd-spice-option]');
      if (!input || input.disabled) return;

      // Own the select/deselect for both radio and label text
      event.preventDefault();
      event.stopPropagation();

      var wasChecked =
        pendingOption && pendingOption.input === input
          ? pendingOption.wasChecked
          : !!input.checked;
      pendingOption = null;

      toggleOptionSelection(root, input, wasChecked);
    });

    root.addEventListener('keydown', function (event) {
      if (event.key !== ' ' && event.key !== 'Enter') return;
      var input = event.target.closest
        ? event.target.closest('[data-fcd-spice-option]')
        : null;
      if (!input || !root.contains(input) || input.disabled) return;
      event.preventDefault();
      toggleOptionSelection(root, input, !!input.checked);
    });

    var qty = document.querySelector(
      '#Quantity, .custom-product-right .product-form input[name="quantity"]'
    );
    if (qty && !qty.dataset.fcdSpicePriceBound) {
      qty.dataset.fcdSpicePriceBound = 'true';
      qty.addEventListener('change', function () {
        updateDisplayedPrice(root);
      });
      qty.addEventListener('input', function () {
        updateDisplayedPrice(root);
      });
    }

    var qtyWrap = document.querySelector('.custom-quantity-container');
    if (qtyWrap && !qtyWrap.dataset.fcdSpicePriceBound) {
      qtyWrap.dataset.fcdSpicePriceBound = 'true';
      qtyWrap.addEventListener('click', function (event) {
        if (!event.target.closest('.custom-qty-btn')) return;
        window.setTimeout(function () {
          updateDisplayedPrice(root);
        }, 0);
      });
    }
  }

  function hideNativeSpice(hide) {
    if (!hide) return;
    var styleId = 'fcd-spice-addons-hide-native';
    if (document.getElementById(styleId)) return;
    var style = document.createElement('style');
    style.id = styleId;
    style.textContent =
      '#spiceaddonsappid,' +
      '.shopify-block[id*="spice_addons"],' +
      '.sg-pro-subtotal-price{display:none!important;}';
    document.head.appendChild(style);
  }

  function syncInitialSelections(root) {
    root.querySelectorAll('.fcd-spice-addons__group').forEach(function (groupEl) {
      syncOptionVisuals(groupEl);
      updateGroupSelectedLabel(groupEl);
      syncGroupToSpice(groupEl);
    });
  }

  function getProductForm() {
    return document.querySelector(PRODUCT_FORM_SEL);
  }

  function getMainVariantId(form) {
    if (!form) return '';
    var input = form.querySelector('input[name="id"], select[name="id"]');
    return input && input.value ? String(input.value) : '';
  }

  function getSelectedAddonCartItems(root, quantity, excludeVariantId, parentVariantId) {
    var items = [];
    var seen = {};
    var exclude = excludeVariantId ? String(excludeVariantId) : '';
    var parentId = parentVariantId
      ? String(parentVariantId)
      : exclude
        ? exclude
        : '';
    root.querySelectorAll('[data-fcd-spice-option]:checked').forEach(function (input) {
      var variantId = input.getAttribute('data-variant-id');
      if (!variantId || variantId === '0') return;
      if (exclude && String(variantId) === exclude) return;
      if (seen[variantId]) return;
      seen[variantId] = true;
      var lineItem = {
        id: Number(variantId),
        quantity: quantity,
        properties: {
          _spice_addon: 'true'
        }
      };
      if (parentId) {
        lineItem.properties._parent_variant = parentId;
      }
      items.push(lineItem);
    });
    return items;
  }

  function getCartAddUrl() {
    return (
      (window.Theme && Theme.routes && Theme.routes.cart_add_url) ||
      (window.routes && window.routes.cart_add_url) ||
      '/cart/add.js'
    );
  }

  /**
   * Add only the PDP product using the theme product form fields (FormData).
   * Does not include Spice addons — those are posted separately.
   */
  async function addMainProductFromForm(form, sectionIds) {
    var formData = new FormData(form);
    (sectionIds || []).forEach(function (id) {
      formData.append('sections', id);
    });

    var response = await fetch(getCartAddUrl(), {
      method: 'POST',
      headers: {
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      body: formData
    }).then(function (res) {
      return res.json().then(function (json) {
        return { ok: res.ok, json: json };
      });
    });

    if (!response.ok || (response.json && response.json.status)) {
      var message =
        (response.json && (response.json.description || response.json.message)) ||
        'Unable to add product to cart.';
      throw new Error(message);
    }

    return response.json;
  }

  function collectCartSectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component').forEach(function (el) {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        ids.push(el.dataset.sectionId);
      }
    });
    return ids;
  }

  function setAddToCartBusy(form, busy) {
    if (!form) return;
    var button = form.querySelector(
      'button[type="submit"], .add-to-cart-button, button.add-to-cart-button'
    );
    if (!button) return;
    button.disabled = !!busy;
    button.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  function openCartDrawer() {
    if (
      window.Shopify &&
      Shopify.actions &&
      typeof Shopify.actions.openCart === 'function'
    ) {
      try {
        Shopify.actions.openCart();
        return;
      } catch (err) {
        /* fall through */
      }
    }
    var drawer = document.querySelector('theme-drawer#cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    }
  }

  function refreshCartSectionsFallback(sectionIds) {
    if (!sectionIds || !sectionIds.length) {
      openCartDrawer();
      return Promise.resolve();
    }

    return import('@theme/section-renderer')
      .then(function (mod) {
        var sectionRenderer = mod.sectionRenderer;
        return Promise.all(
          sectionIds.map(function (id) {
            return sectionRenderer.renderSection(id, {
              cache: false,
              mode: 'hydration'
            });
          })
        );
      })
      .catch(function (err) {
        console.warn('[FCD Spice Addons] Section refresh failed', err);
      })
      .then(function () {
        openCartDrawer();
      });
  }

  /**
   * @param {Array} items - addon line items for /cart/add.js
   * @param {string[]} sectionIds
   * @param {{ mainForm?: HTMLFormElement }} [options] - mainForm: add the PDP product from
   *   this form first, inside the same CartLinesUpdateEvent, so the drawer opens once and
   *   re-renders once for the whole add.
   */
  async function addItemsToCart(items, sectionIds, options) {
    var cartAddUrl = getCartAddUrl();
    var mainForm = options && options.mainForm;

    var payload = {
      items: items,
      sections: sectionIds.join(',')
    };

    var eventsMod = null;
    try {
      eventsMod = await import('@shopify/events');
    } catch (err) {
      eventsMod = null;
    }

    var CartLinesUpdateEvent = eventsMod && eventsMod.CartLinesUpdateEvent;
    var deferred =
      CartLinesUpdateEvent && typeof CartLinesUpdateEvent.createPromise === 'function'
        ? CartLinesUpdateEvent.createPromise()
        : null;

    if (CartLinesUpdateEvent && deferred) {
      var lines = items.map(function (item) {
        return {
          merchandiseId: String(item.id),
          quantity: item.quantity
        };
      });
      if (mainForm) {
        lines.unshift({ merchandiseId: getMainVariantId(mainForm), quantity: getQuantity() });
      }
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: lines,
          promise: deferred.promise
        })
      );
    }

    if (mainForm) {
      try {
        await addMainProductFromForm(mainForm, []);
      } catch (err) {
        if (deferred) deferred.reject(err);
        throw err;
      }
    }

    var response = await fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    }).then(function (res) {
      return res.json().then(function (json) {
        return { ok: res.ok, json: json };
      });
    });

    if (!response.ok || (response.json && response.json.status)) {
      var message =
        (response.json && (response.json.description || response.json.message)) ||
        'Unable to add items to cart.';
      if (deferred) {
        deferred.reject(new Error(message));
      }
      throw new Error(message);
    }

    var ajaxCart = await fetch(
      ((window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart') + '.js',
      {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      }
    ).then(function (res) {
      if (!res.ok) throw new Error('Failed to refresh cart');
      return res.json();
    });

    if (deferred && CartLinesUpdateEvent) {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'fcd-spice-addons',
          itemCount: items.reduce(function (sum, item) {
            return sum + item.quantity;
          }, 0),
          sections: response.json && response.json.sections,
          didError: false
        }
      });
    } else {
      await refreshCartSectionsFallback(sectionIds);
    }

    return { cart: ajaxCart, addResponse: response.json };
  }

  function getValidAddonItems(root, form) {
    // Re-sync native Spice (if mounted) right before add
    root.querySelectorAll('.fcd-spice-addons__group').forEach(function (groupEl) {
      syncGroupToSpice(groupEl);
    });

    return getSelectedAddonCartItems(root, getQuantity(), getMainVariantId(form)).filter(
      function (item) {
        return item.id && !Number.isNaN(item.id) && item.quantity > 0;
      }
    );
  }

  function runAddonAdd(form, addonItems, options) {
    var sectionIds = collectCartSectionIds();
    form.dataset.fcdSpiceCartSubmitting = 'true';
    setAddToCartBusy(form, true);
    // fcd-pdp-atc-error.js: clear any previous message under the button, and remember what was
    // asked for so a stock-limited add can be told apart from a failed one
    var lines = addonItems.map(function (item) {
      return { merchandiseId: String(item.id), quantity: item.quantity };
    });
    if (options && options.mainForm) {
      lines.unshift({ merchandiseId: getMainVariantId(form), quantity: getQuantity() });
    }
    form.dispatchEvent(
      new CustomEvent('apollo:add-to-cart-start', { bubbles: true, detail: { lines: lines } })
    );

    var before = (options && options.after) || Promise.resolve();

    return before
      .catch(function () {
        /* the theme reports its own add errors; still add the addons */
      })
      .then(function () {
        return addItemsToCart(addonItems, sectionIds, options);
      })
      .catch(function (err) {
        console.warn('[FCD Spice Addons] Add to cart failed', err);
        // Shown under the button by fcd-pdp-atc-error.js; fall back to an alert if the
        // message element isn't on the page.
        if (form.querySelector('[data-product-atc-error]')) {
          form.dispatchEvent(
            new CustomEvent('apollo:add-to-cart-failed', {
              bubbles: true,
              detail: { message: (err && err.message) || '' }
            })
          );
        } else {
          window.alert((err && err.message) || 'Unable to add selected products to cart.');
        }
      })
      .finally(function () {
        form.dataset.fcdSpiceCartSubmitting = 'false';
        setAddToCartBusy(form, false);
      });
  }

  function bindAddToCart(root) {
    var form = getProductForm();
    if (!form || form.dataset.fcdSpiceCartBound === 'true') return;
    form.dataset.fcdSpiceCartBound = 'true';

    // Horizon handles `on:submit` from a document-level capture listener (component.js),
    // which runs before anything bound on the form, so stopping the submit here is too late:
    // product-form-component had already added the PDP product, and we added it a second
    // time (Rebuy's "Double add-to-cart detected and compensated" was quietly fixing it on
    // live). So the add is taken over at the click instead — preventDefault on the submit
    // button's click means no submit event is fired at all.
    form.addEventListener(
      'click',
      function (event) {
        var button = event.target.closest(
          'button[type="submit"], .add-to-cart-button, button.add-to-cart-button'
        );
        if (!button || !form.contains(button)) return;
        if (form.dataset.fcdSpiceCartSubmitting === 'true') {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }

        var addonItems = getValidAddonItems(root, form);

        // No addons: do not intervene — the theme product form adds the PDP product alone.
        if (!addonItems.length) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        if (!form.reportValidity()) return;

        runAddonAdd(form, addonItems, { mainForm: form });
      },
      true
    );

    // Remember the theme's own add (dispatched synchronously from its submit handler, by the
    // product-form-component that wraps this form) so the fallback below can wait for it.
    // Two concurrent /cart/add.js calls can overwrite each other and drop a line.
    document.addEventListener(STANDARD_CART_LINES_UPDATE, function (event) {
      var source = event.target;
      if (!(source instanceof Element) || !event.promise) return;
      if (source.contains(form) || form.contains(source)) {
        form._fcdThemeAdd = event.promise;
      }
    });

    // Fallback for a submit that didn't come through the button click (e.g. requestSubmit()):
    // the theme has already added the PDP product by now, so only the addons are added here.
    form.addEventListener(
      'submit',
      function (event) {
        if (form.dataset.fcdSpiceCartSubmitting === 'true') {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }

        var addonItems = getValidAddonItems(root, form);
        if (!addonItems.length) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        var themeAdd = form._fcdThemeAdd;
        form._fcdThemeAdd = null;
        runAddonAdd(form, addonItems, { after: themeAdd });
      },
      true
    );
  }

  function hideAddonsBlock(root, status, mount) {
    if (status) {
      status.textContent = '';
      status.hidden = true;
    }
    if (mount) mount.innerHTML = '';
    if (root) {
      root.hidden = true;
      root.setAttribute('aria-hidden', 'true');
    }
  }

  async function boot(root) {
    if (!root || root.dataset.booted === 'true') return;
    root.dataset.booted = 'true';

    var cfg = getConfig(root);
    root._fcdSpiceConfig = cfg;
    var status = root.querySelector('[data-fcd-spice-status]');
    var mount = root.querySelector('[data-fcd-spice-mount]');

    hideNativeSpice(cfg.hideNativeSpice !== false);
    // Bind early so addon ATC works even while groups are still loading
    bindAddToCart(root);

    try {
      if (status) status.textContent = 'Loading addons…';

      var spiceData = await fetchSpiceGroups(cfg);
      if (!spiceData.is_active) {
        hideAddonsBlock(root, status, mount);
        return;
      }

      var productIds = [];
      (spiceData.groups || []).forEach(function (group) {
        (group.addons || []).forEach(function (addon) {
          if (addon.product_id) productIds.push(String(addon.product_id));
        });
      });
      productIds = productIds.filter(function (id, i, arr) {
        return arr.indexOf(id) === i;
      });

      var productsById = await fetchProductsByIds(
        productIds,
        cfg.storefrontAccessToken,
        cfg.apiVersion
      );
      var model = buildModel(spiceData, productsById);

      if (!model.length) {
        hideAddonsBlock(root, status, mount);
        return;
      }

      if (mount) {
        mount.innerHTML = renderHtml(model);
      }
      if (status) status.hidden = true;
      bindInteractions(root);
      syncInitialSelections(root);
      updateDisplayedPrice(root);

      watchPriceRewrites(root);
      document.addEventListener('apollo:product-options-changed', function () {
        updateDisplayedPrice(root);
      });

      root._fcdSpiceModel = model;
      root._fcdSpiceRaw = spiceData;
      document.dispatchEvent(
        new CustomEvent('apollo:fcd-spice-addons-ready', {
          detail: { root: root, model: model, raw: spiceData }
        })
      );
    } catch (err) {
      hideAddonsBlock(root, status, mount);
      console.warn('[FCD Spice Addons]', err);
    }
  }

  function init() {
    document.querySelectorAll(ROOT_SEL).forEach(boot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
